import { HttpErrorResponse, HttpEvent, HttpEventType, HttpResponse } from '@angular/common/http';
import { Component, ElementRef, Inject, ViewChild } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Observable, PartialObserver, Subject } from 'rxjs';
import { FileExtensions } from 'src/app/enums/file-extensions.enum';
import { Prescription } from 'src/app/models/prescription.model';
import { CallService } from 'src/app/services/call.service';
import { PrescriptionService } from 'src/app/services/prescription.service';
import { ToastService } from 'src/app/services/toast.service';
import { PrescriptionStatus } from 'src/app/enums/prescription-status.enum';
import * as _ from 'lodash';
import { AttachmentService } from 'src/app/services/attachment.service';
import { FileService } from 'src/app/services/file.service';

export interface PrescriptionAttachment extends Partial<Prescription> {
  fileId: string;
  progress: number | null;
  error?: boolean;
  url?: string;
}

@Component({
  selector: 'app-upload-external-prescription-dialog',
  templateUrl: './upload-external-prescription-dialog.component.html',
  styleUrls: ['./upload-external-prescription-dialog.component.css'],
  standalone: false
})
export class UploadExternalPrescriptionDialogComponent {
  formGroup = this.buildForm();

  uploading = false;
  done = false;
  fileExtensions = FileExtensions;
  attachments: PrescriptionAttachment[] = [];
  prescriptionStatus = PrescriptionStatus;

  @ViewChild('attachmentContainer') attachmentContainer!: ElementRef<HTMLDivElement>;

  constructor(
    private formBuilder: FormBuilder,
    private fileService: FileService,
    private callService: CallService,
    private toastService: ToastService,
    private attachmentService: AttachmentService<PrescriptionAttachment>,
    private prescriptionService: PrescriptionService,
    @Inject(MAT_DIALOG_DATA) public data: { callId: string; prescriptions: Prescription[] }
  ) {
    this.attachments = (data.prescriptions as PrescriptionAttachment[]) || [];
  }

  buildForm(): FormGroup {
    return this.formBuilder.group({
      name: ['', Validators.required]
    });
  }

  addFile($event: Event): void {
    const formData = new FormData();
    const input = $event.target as HTMLInputElement;
    const file = input.files?.[0];

    if (this.fileService.checkMaxSizeExceeded($event)) return;

    formData.append('medicineName', this.name.value);
    formData.append('fileName', file.name);
    formData.append('fileSize', `${file.size}`);
    formData.append('externalPrescription', file as File);
    input.value = '';

    const fileId = `${Date.now() + Math.random()}`;
    this.attachments.push({
      fileId,
      externalFileName: file?.name,
      externalFileSize: file?.size,
      medicines: [{ active: this.name.value }],
      progress: 0
    });

    this.name.reset();
    this.uploadFileSubscriber(formData, fileId);

    setTimeout(() => (this.attachmentContainer.nativeElement.scrollTop = this.attachmentContainer.nativeElement.scrollHeight), 0);
  }

  uploadFileSubscriber(formData: FormData, fileId: string): void {
    this.uploadFile(formData, fileId).subscribe((fileProgress: PrescriptionAttachment) => {
      if (fileProgress.error) this.attachments = this.attachments.filter((file) => file.fileId !== fileProgress.fileId);
      else {
        let file = this.attachments.find((file) => file.fileId === fileProgress.fileId);
        if (file) {
          file.progress = fileProgress.progress;
          // attachment progress completed
          if (fileProgress.id) {
            file = _.merge(file, fileProgress);
            this.attachmentService.attachments = this.attachments;
          }
        }
      }
    });
  }

  uploadFile(formData: FormData, fileId: string): Observable<PrescriptionAttachment> {
    const progressSubject = new Subject<PrescriptionAttachment>();

    const fileObserver: PartialObserver<HttpEvent<Prescription>> = {
      next: (event: HttpEvent<Prescription>) => {
        if (event.type === HttpEventType.UploadProgress) {
          const progress = Math.round((100 * event.loaded) / (event.total as number));
          progressSubject.next({ fileId, progress, error: false });
          this.uploading = true;
        } else if (event instanceof HttpResponse) {
          progressSubject.next({ ...event.body, fileId, progress: null, error: false } as PrescriptionAttachment);
          progressSubject.complete();
          this.toastService.openSuccessToast('default_upload_file_success');
          this.uploading = false;
          this.done = true;
        }
      },
      error: (error: HttpErrorResponse) => {
        progressSubject.next({ fileId, progress: null, error: true });
        progressSubject.complete();
        this.toastService.openErrorToast(error?.error?.message || 'default_upload_file_error');
        this.uploading = false;
      }
    };
    this.prescriptionService.uploadExternal(this.data.callId, formData).subscribe(fileObserver);

    return progressSubject.asObservable();
  }

  async openDetail(attachment: PrescriptionAttachment): Promise<void> {
    this.callService
      .getPrescriptionPdf(this.data.callId, attachment.id)
      .then((response) => {
        if (!response?.url) {
          this.toastService.openErrorToast();
          return;
        }

        window.open(response.url);
      })
      .catch((error: HttpErrorResponse) => this.toastService.openSuccessToast(error.error?.message));
  }

  remove(prescriptionId: string): void {
    this.callService
      .updateExternalPrescriptionStatusToDeleted(this.data.callId, prescriptionId)
      .then(() => {
        this.toastService.openSuccessToast();
        this.attachments = this.attachments.filter((prescription) => prescriptionId !== prescription.id);
        this.attachmentService.attachments = this.attachments;
      })
      .catch((error: HttpErrorResponse) => this.toastService.openSuccessToast(error.error?.message));
  }

  get name() {
    return this.formGroup.get('name') as FormControl;
  }
}
